import { globalThemeManager } from 'foundations-theming/utils';
import { COLOR_OVERRIDE_CONFIGS, FONT_OVERRIDE_CONFIGS, BORDER_RADIUS_OVERRIDE_CONFIGS, deriveGenericColorVariants, SURFACE_STYLE_OVERRIDE_CONFIG } from './registry';
import { getPortalName, setPortalRuntimeGlobals } from './metadata';

// Theme category prefixes for parsing trellis-prefixed keys
const THEME_CATEGORY_PREFIXES = {
  Color: 'color',
  BorderRadius: 'borderRadius',
  BorderWidth: 'borderWidth',
  BoxShadow: 'boxShadow',
  Space: 'space',
  Typography: 'typography'
};
const defaultThemeOverrides = {
  color: {
    // Neutral/Grayscale colors for UI chrome
    'fill-primary-default': '#121212',
    // dark charcoal gray
    'fill-secondary-default': '#5C5C5C',
    // medium gray
    'fill-tertiary-default': '#8A8A8A',
    // light gray
    // Interactive text color
    'text-interactive-default': '#666666',
    // medium gray
    // Semantic status colors
    'fill-accent-green-default': '#2E7D32',
    // success/positive (dark green)
    'fill-accent-red-default': '#C62828',
    // error/alert (dark red)
    'fill-caution-default': '#ED9C28',
    // warning/caution (orange)
    'fill-info-default': '#0277BD' // info (deep blue)
  }
};

// Apply derivation functions to default overrides to generate all color tokens.
// This ensures the default theme has all current color tokens we override even without brand/theme settings.
function applyDefaultOverrides() {
  const colorOverrides = {};

  // Derive all variants for each default base color
  Object.entries(COLOR_OVERRIDE_CONFIGS).forEach(([tokenName, {
    deriveVariants
  }]) => {
    var _defaultThemeOverride;
    const baseColor = (_defaultThemeOverride = defaultThemeOverrides.color) === null || _defaultThemeOverride === void 0 ? void 0 : _defaultThemeOverride[tokenName];
    if (baseColor) {
      Object.assign(colorOverrides, deriveVariants(baseColor));
    }
  });

  // Derive surface color variants (uses 'light' | 'dark' style instead of hex)
  Object.assign(colorOverrides, SURFACE_STYLE_OVERRIDE_CONFIG.deriveVariants('light'));
  return {
    color: colorOverrides
  };
}
function isFontValue(value) {
  return value !== null && value !== undefined && typeof value === 'object' && 'font' in value && typeof value.font === 'string';
}
export function deepMerge(target, source) {
  const result = Object.assign({}, target);
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = result[key];
      if (sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue) && targetValue && typeof targetValue === 'object' && !Array.isArray(targetValue)) {
        result[key] = deepMerge(targetValue, sourceValue);
      } else if (sourceValue !== undefined) {
        result[key] = sourceValue;
      }
    }
  }
  return result;
}
function camelToKebab(str) {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '');
}

// Expected format: `trellis${themeCategory}${tokenName}`
function parseThemeSettingKey(key) {
  // TODO: fix this for font family
  if (key === 'fontFamily') {
    return {
      category: 'typography',
      tokenName: 'fontFamily'
    };
  }

  // Handle trellis-prefixed keys
  if (key.startsWith('trellis')) {
    // Remove 'trellis' prefix
    const tokenKeyWithoutPrefix = key.replace(/^trellis/, '');

    // Find matching category prefix
    for (const [prefix, category] of Object.entries(THEME_CATEGORY_PREFIXES)) {
      if (tokenKeyWithoutPrefix.startsWith(prefix)) {
        const tokenName = camelToKebab(tokenKeyWithoutPrefix.replace(new RegExp(`^${prefix}`), ''));
        return {
          category: category,
          tokenName
        };
      }
    }
  }
  return {
    category: null,
    tokenName: key
  };
}
function extractValue(value) {
  if (typeof value === 'string') {
    return value;
  }
  // TODO: theme fields pass through a single number as the value without the unit "px"
  // We need to figure out support for applying the px value to the theme which is what
  // trellis expects. Need to confirm where we set numbers and units to confirm approach.
  if (typeof value === 'number') {
    return String(value);
  }

  // TODO: likely will need more handling for various content theme fields
  if (value && typeof value === 'object') {
    // Handle ColorValue - check for properties unique to ColorValue
    if ('hex' in value || 'rgba' in value || 'rgb' in value || 'opacity' in value) {
      return value.hex || value.rgba || value.color || undefined;
    }
    // Handle FontFieldValue
    if ('css' in value && value.css) {
      return value.css;
    }
  }
  return undefined;
}

// Map brand settings to theme overrides using color variant derivation
function mapBrandSettingsToTheme(brandSettings) {
  const colorOverrides = {};
  const fontOverrides = {};

  // Derive colors for each configured color token
  Object.values(COLOR_OVERRIDE_CONFIGS).forEach(({
    brandField,
    deriveVariants
  }) => {
    if (brandField === null) return;
    const baseColor = extractValue(brandSettings[brandField]);
    if (baseColor) {
      Object.assign(colorOverrides, deriveVariants(baseColor));
    }
  });
  Object.values(FONT_OVERRIDE_CONFIGS).forEach(({
    brandField,
    deriveVariants
  }) => {
    const fontValue = brandSettings[brandField];
    if (isFontValue(fontValue)) {
      Object.assign(fontOverrides, deriveVariants(fontValue.font));
    }
  });
  const result = {};
  if (Object.keys(colorOverrides).length > 0) {
    result.color = colorOverrides;
  }
  if (Object.keys(fontOverrides).length > 0) {
    result.typography = fontOverrides;
  }
  return result;
}

/**
 * Map theme settings to theme overrides
 * Handles trellis-prefixed keys and special cases like fontFamily
 * Derives all related tokens (fill variants, borders, text) if only default is provided
 * Optionally applies custom theme settings mappings with variant derivation
 */
export function mapThemeSettingsToTheme(themeSettings, tokenOverrides) {
  const result = {};
  // Replace pre-defined token values with values from theme settings
  Object.keys(themeSettings).forEach(key => {
    const value = themeSettings[key];
    if (!value) return;
    const {
      category,
      tokenName
    } = parseThemeSettingKey(key);
    if (!category) return;
    const extractedValue = extractValue(value);
    if (!extractedValue) return;
    if (!result[category]) {
      result[category] = {};
    }
    result[category][tokenName] = extractedValue;
  });

  // Derive variants for color tokens
  if (!result.color) {
    result.color = {};
  }
  const colorMap = result.color;
  Object.values(COLOR_OVERRIDE_CONFIGS).forEach(({
    themeSettingField,
    deriveVariants
  }) => {
    if (themeSettingField === null) return;
    const themeValue = themeSettings[themeSettingField];
    const baseColor = extractValue(themeValue);
    if (baseColor) {
      Object.assign(colorMap, deriveVariants(baseColor));
    }
  });

  // Derive surface color variants (uses 'light' | 'dark' style instead of hex)
  const surfaceStyle = themeSettings[SURFACE_STYLE_OVERRIDE_CONFIG.themeSettingField];
  if (surfaceStyle) {
    Object.assign(colorMap, SURFACE_STYLE_OVERRIDE_CONFIG.deriveVariants(surfaceStyle));
  }

  // Derive variants for border radius tokens
  if (!result.borderRadius) {
    result.borderRadius = {};
  }
  const borderRadiusMap = result.borderRadius;
  BORDER_RADIUS_OVERRIDE_CONFIGS.forEach(({
    themeSettingField,
    deriveVariants
  }) => {
    const themeValue = themeSettings[themeSettingField];
    const baseChoice = extractValue(themeValue);
    if (baseChoice) {
      const variants = deriveVariants(baseChoice);
      if (variants) {
        Object.assign(borderRadiusMap, variants);
      }
    }
  });
  Object.values(FONT_OVERRIDE_CONFIGS).forEach(({
    themeSettingField,
    deriveVariants
  }) => {
    const fontValue = themeSettings[themeSettingField];
    if (isFontValue(fontValue)) {
      if (!result.typography) {
        result.typography = {};
      }
      Object.assign(result.typography, deriveVariants(fontValue.font));
    }
  });
  if (tokenOverrides) {
    for (const [cmsField, targetToken] of Object.entries(tokenOverrides)) {
      if (!(cmsField in themeSettings)) {
        console.warn(`Token mapping references unknown field: "${cmsField}". Check that this field exists within your theme.`);
        continue;
      }
      const fieldValue = themeSettings[cmsField];
      const extractedColor = extractValue(fieldValue);
      if (extractedColor) {
        const variants = deriveGenericColorVariants(extractedColor, targetToken);
        Object.assign(colorMap, variants);
      }
    }
  }
  return result;
}

// This is not being used right now but I think we will keep around
// until final patterns are solidified. I think we need something like this
// if the portal is set to canvas and not trellis and we'd need to override the canvas theme.
// But I need to verify this in practice before we decide to keep or remove it.
// function applyOverridesToAllVariants(
//   baseTheme: Record<string, Theme>,
//   overrides: DeepPartial<Theme>
// ): Record<string, Theme> {
//   const result: Record<string, Theme> = {};
//   const themeVariants = [
//     'canvas-light',
//     'trellis-light',
//     'trellis-dark',
//   ] as const;

//   themeVariants.forEach((variant) => {
//     if (baseTheme[variant]) {
//       result[variant] = deepMerge(baseTheme[variant], overrides);
//     }
//   });

//   return result;
// }

/**
 * Merges theme overrides in priority order:
 * 1. Default overrides (lowest priority)
 * 2. Brand settings (medium priority)
 * 3. Theme settings (highest priority)
 * 4. Custom theme settings (highest priority - applied within theme settings)
 */
export function buildThemeOverrides(brandAndThemeData, tokenOverrides) {
  var _brandAndThemeData$co, _brandAndThemeData$co2;
  let result = applyDefaultOverrides();

  // Apply brand settings overrides
  if (brandAndThemeData !== null && brandAndThemeData !== void 0 && (_brandAndThemeData$co = brandAndThemeData.context) !== null && _brandAndThemeData$co !== void 0 && _brandAndThemeData$co.brandSettings) {
    const brandOverrides = mapBrandSettingsToTheme(brandAndThemeData.context.brandSettings);
    result = deepMerge(result, brandOverrides);
  }

  // Apply theme settings overrides (highest priority)
  if (brandAndThemeData !== null && brandAndThemeData !== void 0 && (_brandAndThemeData$co2 = brandAndThemeData.context) !== null && _brandAndThemeData$co2 !== void 0 && _brandAndThemeData$co2.themeSettings) {
    const themeOverrides = mapThemeSettingsToTheme(brandAndThemeData.context.themeSettings, tokenOverrides);
    result = deepMerge(result, themeOverrides);
  }
  return result;
}
function loadPreviewListener() {
  import('./preview').then(({
    initializePreviewListener
  }) => {
    initializePreviewListener();
  }).catch(error => {
    console.error('Failed to initialize preview listener:', error);
  });
}

/**
 * @deprecated - use getPortalData instead
 * Creates a modified theme by applying customer defined brand and theme data to the base trellis theme.
 *
 * @param baseTheme - The base trellis theme
 * @returns A final modified trellis theme with the customer's brand and theme settings applied
 *
 */
export async function createPortalTheme(baseTheme) {
  const portalName = getPortalName();
  const isThemePreviewer = new URLSearchParams(window.location.search).get('isPortalPreview') === 'true';
  // TODO: Remove fallback
  // Using fallback for now as I test within the theme previewer
  const previewToken = new URLSearchParams(window.location.search).get('hs_preview') || 'AlQmNscn-193884612432';
  const requestUrl = `/hs/portals/${portalName}/brand${isThemePreviewer ? `?hs_preview=${previewToken}` : ''}`;
  const brandAndThemeData = await fetch(requestUrl).then(response => response.json()).catch(error => {
    console.error('Error fetching brand and theme data', error);
    // Return empty context to allow theme creation to proceed with defaults
    return {
      context: {}
    };
  });
  const themeOverrides = buildThemeOverrides(brandAndThemeData);
  const modifiedTrellisTheme = deepMerge(baseTheme, themeOverrides);

  // Initialize preview listener for hot reload in the theme previewer
  if (isThemePreviewer) {
    loadPreviewListener();
  }
  return modifiedTrellisTheme;
}

/**
 * @deprecated - use initializePortal instead
 * Fetches brand and theme data and creates a modified theme by applying that data to the base trellis theme.
 *
 * @returns Source data, and the final modified trellis theme with the customer's brand and theme settings applied
 *
 */
export async function getPortalData(baseTheme) {
  const portalName = getPortalName();
  const isThemePreviewer = new URLSearchParams(window.location.search).get('isPortalPreview') === 'true';
  const previewToken = new URLSearchParams(window.location.search).get('hs_preview');
  const requestUrl = `/hs/portals/${portalName}/brand${isThemePreviewer ? `?hs_preview=${previewToken}` : ''}`;
  const portalData = await fetch(requestUrl).then(response => response.json()).catch(error => {
    console.error('Error fetching brand and theme data', error);
    // Return empty context to allow theme creation to proceed with defaults
    return {
      context: {}
    };
  });
  const portalTheme = getPortalTheme(baseTheme, portalData);

  // Initialize preview listener for hot reload in the theme previewer
  if (isThemePreviewer) {
    loadPreviewListener();
  }
  return {
    portalTheme,
    portalData
  };
}
function getPortalTheme(baseTheme, brandAndThemeData, tokenOverrides) {
  const themeOverrides = buildThemeOverrides(brandAndThemeData, tokenOverrides);
  const modifiedTrellisTheme = deepMerge(baseTheme, themeOverrides);
  return modifiedTrellisTheme;
}

// Forces consumers to use trellis-light theme for consistency
function setPortalTheme(modifiedPortalTheme) {
  globalThemeManager.setTheme({
    themeOverrides: modifiedPortalTheme,
    themeName: 'trellis',
    themeMode: 'light'
  });
}
function appendElementsToDocument(target, elements = []) {
  if (!(elements !== null && elements !== void 0 && elements.length)) return;
  const fragment = document.createDocumentFragment();
  elements.forEach(element => {
    var _element$children;
    const el = document.createElement(element.tag);
    if (element.id) {
      el.id = element.id;
    }
    if (element.attributes) {
      Object.entries(element.attributes).forEach(([key, value]) => {
        el.setAttribute(key, value);
      });
    }
    if ((_element$children = element.children) !== null && _element$children !== void 0 && _element$children.length) {
      const textContent = element.children.filter(child => child.tag === '#text').map(child => child.text).join('');
      if (textContent) {
        el.textContent = textContent;
      }
    }
    fragment.appendChild(el);
  });
  target.appendChild(fragment);
}
/**
 * Fetches brand and theme data
 * Creates a modified theme by applying that data to the base trellis theme
 * Enforces the use of the `trellis-light` theme object
 * Automatically applies the theme to the global theme manager
 *
 * @param config - Optional configuration object
 * @param config.tokenOverrides - Mapping of CMS theme fields to Trellis tokens with variant derivation
 * @returns Useful portal data and applies the modified theme object to the global theme manager
 *
 */
export async function initializePortal(config) {
  const tokenOverrides = config === null || config === void 0 ? void 0 : config.tokenOverrides;
  const baseTheme = globalThemeManager.getTheme();
  const portalName = getPortalName();
  setPortalRuntimeGlobals();
  const params = new URLSearchParams(window.location.search);
  const isThemePreviewer = params.get('isPortalPreview') === 'true';
  // Used to test the default colors for a portal instead of the user defined colors
  const useDefaults = params.get('useDefaults') === 'true';
  const previewToken = params.get('hs_preview');
  const themeSettingsVersion = params.get('theme_settings_version');
  const basePath = `/hs/portals/${portalName}/brand`;
  let queryString = '';
  if (isThemePreviewer) {
    const brandParams = new URLSearchParams();
    if (previewToken) {
      brandParams.set('hs_preview', previewToken);
    }
    if (themeSettingsVersion) {
      brandParams.set('theme_settings_version', themeSettingsVersion);
    }
    queryString = brandParams.toString();
  }
  const requestUrl = queryString ? `${basePath}?${queryString}` : basePath;

  // TODO: expand type
  const portalData = useDefaults ? {
    context: {}
  } : await fetch(requestUrl).then(response => response.json()).catch(error => {
    console.error('Error fetching brand and theme data', error);
    return {
      context: {}
    };
  });
  appendElementsToDocument(document.head, portalData.headElements);
  appendElementsToDocument(document.body, portalData.footerElements);
  const portalTheme = getPortalTheme(baseTheme, portalData, tokenOverrides);

  // Initialize preview listener for hot reload in the theme previewer
  if (isThemePreviewer) {
    loadPreviewListener();
  }
  setPortalTheme(portalTheme);
  return {
    portalTheme,
    portalData
  };
}